import { css } from "@emotion/react";
import {
	ChevronDownIcon,
	ChevronRightIcon,
	FolderClosed,
	FolderOpen,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTailwindMediaQueries } from "@/hooks/useTailwindMediaQueries";
import { cn } from "@/lib/utils";
import type { Catalog } from "../../../hooks/useCatalogState";
import {
	type ProductCategory,
	type ProductDepartment,
	type ProductSubCategory,
	useInventoryState,
} from "../../../hooks/useInventoryState";
import { useIsDesktopMediaQuery } from "../../../hooks/useIsDesktopMediaQuery";
import { useThemeState } from "../../../hooks/useThemeState";
import { Button, buttonVariants } from "../../ui/button";
import { SubCategoryControl } from "./SubCategoryControl";

//-------------------------------------------------------------------------------------------------------
export type CategoryControlProps = {
	catalog: Catalog;
	department: ProductDepartment;
	category: ProductCategory;
	isParentFilterActive: boolean;
};
export const CategoryControl: React.FC<CategoryControlProps> = (props) => {
	const mediaQueries = useTailwindMediaQueries();
	const theme = useThemeState().get().theme;
	const inventoryState = useInventoryState();
	const { categoryid, subcategoryid } = useParams();

	const url = `/inventory/${props.catalog.Id}/${props.department.Id}/${props.category.Id}`;
	const hasSubCategories =
		(props.category.SubCategories && props.category.SubCategories.length > 0) ??
		false;

	// Check if this category is selected based on URL parameters
	const isSelected = categoryid === props.category.Id;
	const hasSelectedSubCategory = Boolean(
		subcategoryid &&
			props.category.SubCategories?.some(
				(subCategory) => subCategory.Id === subcategoryid,
			),
	);
	const shouldAutoExpand = isSelected || hasSelectedSubCategory;

	const [isFilterActive, setIsFilterActive] = useState(shouldAutoExpand);

	const isDesktop = useIsDesktopMediaQuery();

	useEffect(() => {
		if (shouldAutoExpand && !isFilterActive) {
			setIsFilterActive(true);
		}
	}, [shouldAutoExpand, isFilterActive]);

	return (
		<React.Fragment key={props.category.Id}>
			{!hasSubCategories && (
				<Link
					to={url}
					className={cn(
						buttonVariants({ variant: "ghost", size: "xs" }),
						"text-sm underline-offset-1 whitespace-normal text-left h-auto flex items-center pt-0 cursor-pointer justify-start",
					)}
					css={css`
						width: 100%;
						color: ${isSelected ? theme.palette.siteSideBarContrastText : theme.palette.siteSideBarText};
						text-decoration: none;
						&:hover {
							text-decoration: underline;
						}`}
					onClick={() => {
						return Promise.resolve().then(() => {
							// Clear search text when category is clicked since they're mutually exclusive
							inventoryState.clearSearchTextAsync();
							if (!isDesktop) {
								inventoryState.setSidebarExpanded(false);
							}
						});
					}}
				>
					<span
						css={css`padding: ${mediaQueries.desktop ? "0" : "4px"} 0 ${mediaQueries.desktop ? "0" : "4px"} 4px;font-size:${mediaQueries.desktop ? ".8rem" : "1rem"};`}
					>
						{props.category.Name}
					</span>
				</Link>
			)}
			{hasSubCategories && (
				<Button
					variant="ghost"
					size="xs"
					className="text-sm underline-offset-1 whitespace-normal text-left h-auto flex items-center pt-0 cursor-pointer"
					onClick={async () => {
						return Promise.resolve().then(() => {
							setIsFilterActive((prev) => !prev);
						});
					}}
				>
					<div
						css={css`
                            flex:1 1 0;
                            display:flex;
                        `}
					>
						<div
							css={css`
							flex:1 1 0;
							cursor:pointer;
							text-indent: -.5em;
							display:flex;
							align-items: center;
						`}
						>
							<div css={css`flex: 0 0 auto;display:flex;align-items:center;`}>
								{!isFilterActive && (
									<FolderClosed size={mediaQueries.desktop ? 16 : 22} />
								)}
								{isFilterActive && (
									<FolderOpen size={mediaQueries.desktop ? 16 : 22} />
								)}
							</div>
							<div
								css={css`flex: 1 1 0;padding:${mediaQueries.desktop ? "0" : "4px"} 14px;font-size:${mediaQueries.desktop ? ".8rem" : "1rem"};`}
							>
								{props.category.Name}
							</div>
						</div>

						{props.category.SubCategories &&
							props.category.SubCategories.length > 0 && (
								<div
									css={css`
								flex:0 0 auto;
								color:${theme.palette.siteSideBarText};
								display:flex;
								align-items:center;
							`}
								>
									{!isFilterActive && <ChevronRightIcon size={16} />}
									{isFilterActive && <ChevronDownIcon size={16} />}
								</div>
							)}
					</div>
				</Button>
			)}
			{isFilterActive &&
				props.category.SubCategories &&
				props.category.SubCategories.map(
					(productSubCategory: ProductSubCategory) => (
						<SubCategoryControl
							key={productSubCategory.Id}
							catalog={props.catalog}
							department={props.department}
							category={props.category}
							subCategory={productSubCategory}
							isParentFilterActive={
								props.isParentFilterActive && isFilterActive
							}
						/>
					),
				)}
		</React.Fragment>
	);
};
